/**
 * Search Utilities for STB Workshop
 * Provides accent-insensitive search functionality
 */

/**
 * Normalizes text by removing accents and converting to lowercase
 * @param {string} text - Text to normalize
 * @returns {string} Normalized text
 */
export const normalizeSearchText = (text) => {
  if (!text || typeof text !== 'string') return '';
  
  // Normalize unicode characters and remove accents
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
};

/**
 * Performs accent-insensitive search match
 * @param {string} text - Text to search in
 * @param {string} searchTerm - Term to search for
 * @returns {boolean} Whether the text contains the search term (accent-insensitive)
 */
export const matchesSearchTerm = (text, searchTerm) => {
  if (!text || !searchTerm) return false;
  
  const normalizedText = normalizeSearchText(text);
  const normalizedSearchTerm = normalizeSearchText(searchTerm);
  
  return normalizedText.includes(normalizedSearchTerm);
};

/**
 * Checks if any field in an array matches the search term
 * @param {Array<string>} fields - Array of text fields to search
 * @param {string} searchTerm - Term to search for
 * @returns {boolean} Whether any field matches the search term
 */
export const matchesAnyField = (fields, searchTerm) => {
  return fields.some(field => matchesSearchTerm(field, searchTerm));
};

/**
 * Highlights search term matches in text (accent-insensitive)
 * @param {string} text - Text to highlight matches in
 * @param {string} searchTerm - Term that was searched for
 * @param {string} className - CSS class for highlighting (default: 'bg-yellow-200 px-0.5 rounded')
 * @returns {string} HTML string with highlighted matches
 */
export const highlightSearchMatches = (text, searchTerm, className = 'bg-yellow-200 px-0.5 rounded') => {
  if (!text || !searchTerm) return text;
  
  const normalizedText = normalizeSearchText(text);
  const normalizedSearchTerm = normalizeSearchText(searchTerm);
  
  if (!normalizedText.includes(normalizedSearchTerm)) return text;
  
  // Create a regex that matches the search term in a case and accent-insensitive way
  const searchRegex = new RegExp(
    searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), // Escape special regex chars
    'gi'
  );
  
  return text.replace(searchRegex, `<mark class="${className}">$&</mark>`);
};

/**
 * Common Spanish accented characters mapping for search
 */
export const SPANISH_ACCENT_MAP = {
  'á': 'a', 'à': 'a', 'ä': 'a', 'â': 'a',
  'é': 'e', 'è': 'e', 'ë': 'e', 'ê': 'e',
  'í': 'i', 'ì': 'i', 'ï': 'i', 'î': 'i',
  'ó': 'o', 'ò': 'o', 'ö': 'o', 'ô': 'o',
  'ú': 'u', 'ù': 'u', 'ü': 'u', 'û': 'u',
  'ñ': 'n',
  'ç': 'c'
};

/**
 * Alternative normalization method using character mapping
 * (Fallback for browsers that don't fully support Unicode normalization)
 * @param {string} text - Text to normalize
 * @returns {string} Normalized text
 */
export const normalizeSearchTextFallback = (text) => {
  if (!text || typeof text !== 'string') return '';
  
  let normalized = text.toLowerCase();
  
  // Replace accented characters
  for (const [accented, plain] of Object.entries(SPANISH_ACCENT_MAP)) {
    normalized = normalized.replace(new RegExp(accented, 'g'), plain);
  }
  
  return normalized;
};

/**
 * Robust normalize function that tries Unicode normalization first, 
 * then falls back to character mapping
 * @param {string} text - Text to normalize
 * @returns {string} Normalized text
 */
export const normalizeSearchTextRobust = (text) => {
  if (!text || typeof text !== 'string') return '';
  
  try {
    // Try Unicode normalization first (modern browsers)
    return normalizeSearchText(text);
  } catch (error) {
    // Fallback to character mapping (older browsers)
    console.warn('Unicode normalization not supported, using fallback method');
    return normalizeSearchTextFallback(text);
  }
};